import { useEffect } from 'react';
import { message } from 'antd';

import { convertRichPaste } from '../pages/OnlyOfficeDocuments/documentsApi';

/**
 * One-keystroke paste: plain Ctrl+V in the report editor keeps the formatting of
 * a report copied from the old Mediff PACS.
 *
 * Mediff puts ONLY `text/rtf` and `text/plain` on the clipboard, and the
 * Document Server's RTF branch is commented out in its own source — so Ctrl+V
 * straight into the editor silently discards the formatting and pastes flat
 * text. "Enhanced Paste" (pacs-paste) already solves that, at the cost of
 * opening a modal and clicking a box first. This removes those two steps.
 *
 * How it works, and why it is shaped this way:
 *
 *   - The clipboard's RTF flavor is readable ONLY from a real `paste` event.
 *     `navigator.clipboard.read()` does not expose `text/rtf` in Chromium at
 *     all, so nothing here can be done ahead of time or on a timer.
 *   - That event happens inside the Document Server's iframe. We can reach it
 *     because DS is served same-origin with the app (proxied at the hosted
 *     sites, dev-server proxy locally) and OnlyOffice's `createIframe()` sets
 *     `allow` but no `sandbox`. Where that is not true, `contentDocument`
 *     throws, nothing is installed, and the Enhanced Paste modal remains.
 *   - We listen in the CAPTURE phase on the editor window, which is the first
 *     point in the event path, so OnlyOffice's own clipboard handler (bound to
 *     an element inside the body) never sees an event we claim.
 *   - We cannot write to the document from here: `PasteHtml` is
 *     `Asc.plugin.executeMethod`, which exists only inside a plugin frame. The
 *     converted HTML is handed to the `pacs-inserter` system plugin, which does
 *     the insert. That plugin is registered by the backend only when the
 *     feature is switched on — so its presence IS the feature flag, and no
 *     separate frontend config can drift out of step with it.
 *
 * Every branch that is not "real RTF, no HTML, inserter ready" returns without
 * touching the event, and the browser hands the paste to OnlyOffice exactly as
 * it does today.
 *
 * See ONE_KEYSTROKE_PASTE_HANDOFF.md for the verified ground facts behind all
 * of the above.
 */

// ── Module-level state ───────────────────────────────────────────────────────
// Singleton on purpose. More than one editor surface can be mounted at a time (a
// report editor with a template modal open over it) and each one calls this
// hook; one scan loop and one listener per editor window is what we want, not
// one of each per surface.

const INSERT_TIMEOUT_MS = 15000;
const SCAN_INTERVAL_MS = 2500;
const TOAST_KEY = 'pacs-oo-rich-paste';

type Pending = { resolve: (ok: boolean, msg: string) => void; timer: number };

/** Inserter plugin frames whose Asc plugin API has come up. */
const readyInserters = new WeakSet<Window>();
/** Editor windows we have installed a listener on -> its remover. */
const hooked = new Map<Window, () => void>();
/** In-flight insert requests, keyed by request id. */
const pendingInserts = new Map<string, Pending>();

let mounted = 0;
let scanTimer: ReturnType<typeof setInterval> | null = null;
let converting = false;
let seq = 0;

// Only used for the server-side log line, which is why "the most recently
// rendered surface" is good enough. With two editors mounted the log can name
// the wrong report; nothing functional reads it.
let currentDocId: number | string | null | undefined = null;

// ── Clipboard reading ────────────────────────────────────────────────────────

const safeGetData = (cd: DataTransfer, type: string): string => {
  try {
    return cd.getData(type) || '';
  } catch {
    return '';
  }
};

/**
 * A `text/rtf` value that does not begin with `{\rtf` is the browser's own
 * custom-data channel echoing something a web page wrote to the clipboard, not
 * real RTF. The backend refuses those too; checking here saves the round trip.
 */
const looksLikeRtf = (rtf: string): boolean => /^\s*\{\s*\\rtf/.test(rtf.slice(0, 200));

// ── Finding the inserter plugin for a given editor document ──────────────────

const INSERTER_SRC = '/onlyoffice-plugins/pacs-inserter/';

/**
 * The plugin frame is served from the BACKEND origin, so its document is not
 * ours to read — but we only need its `contentWindow` to post to, and the
 * `<iframe>` element carrying it lives in the (same-origin) editor document.
 */
const findInserterWindow = (doc: Document, depth = 0): Window | null => {
  if (depth > 3) return null;

  try {
    const frames = Array.from(doc.querySelectorAll('iframe'));

    for (const frame of frames) {
      const src = frame.getAttribute('src') || '';
      if (src.includes(INSERTER_SRC) && frame.contentWindow) return frame.contentWindow;
    }

    // The Document Server has moved plugin frames between containers across
    // versions; recurse through whatever nesting this one uses.
    for (const frame of frames) {
      let childDoc: Document | null = null;
      try {
        childDoc = frame.contentDocument;
      } catch {
        continue; // cross-origin — not ours, and not where our plugin lives
      }
      if (!childDoc) continue;
      const found = findInserterWindow(childDoc, depth + 1);
      if (found) return found;
    }
  } catch {
    /* document went away mid-scan */
  }

  return null;
};

// ── Talking to the inserter plugin ───────────────────────────────────────────

const requestInsert = (target: Window, html: string): Promise<{ ok: boolean; message: string }> =>
  new Promise(resolve => {
    const requestId = `ins${++seq}_${Date.now()}`;

    const timer = window.setTimeout(() => {
      pendingInserts.delete(requestId);
      resolve({ ok: false, message: 'The editor did not answer in time' });
    }, INSERT_TIMEOUT_MS);

    pendingInserts.set(requestId, {
      timer,
      resolve: (ok, msg) => resolve({ ok, message: msg }),
    });

    try {
      target.postMessage({ type: 'pacs_insert_html', requestId, html }, '*');
    } catch {
      window.clearTimeout(timer);
      pendingInserts.delete(requestId);
      resolve({ ok: false, message: 'Could not reach the editor' });
    }
  });

const handleInserterMessage = (event: MessageEvent) => {
  const data = event.data;
  if (!data || typeof data !== 'object') return;

  if (data.type === 'pacs_inserter_ready') {
    // `ready: false` is the plugin saying "I loaded"; it is NOT permission to
    // intercept. Until the Asc handshake has completed the frame cannot write to
    // the document, and claiming a paste we then cannot deliver would lose
    // content that a native paste would at least have landed as flat text.
    if (data.ready && event.source) readyInserters.add(event.source as Window);
    return;
  }

  if (data.type === 'pacs_insert_html_result' && data.requestId) {
    const pending = pendingInserts.get(data.requestId);
    if (!pending) return;
    pendingInserts.delete(data.requestId);
    window.clearTimeout(pending.timer);
    pending.resolve(!!data.ok, data.message || '');
  }
};

// ── The paste handler ────────────────────────────────────────────────────────

const onPaste = (event: Event) => {
  const e = event as ClipboardEvent;

  // Synthetic paste events are OnlyOffice talking to itself.
  if (!e.isTrusted) return;

  const cd = e.clipboardData;
  if (!cd) return;

  // Everything that decides whether this paste is ours must happen NOW:
  // clipboardData is only readable during the event, and the first `await`
  // would put us past that.
  const html = safeGetData(cd, 'text/html');
  if (html) return; // Word, a browser, anything modern — OnlyOffice pastes it better than we can

  const rtf = safeGetData(cd, 'text/rtf') || safeGetData(cd, 'application/rtf');
  if (!rtf || !looksLikeRtf(rtf)) return; // plain text (or nothing) — native paste is already correct

  const doc = (e.target as Node)?.ownerDocument || (e.currentTarget as Window)?.document;
  if (!doc) return;

  const inserter = findInserterWindow(doc);
  if (!inserter || !readyInserters.has(inserter)) return; // feature not wired up here — native paste

  e.preventDefault();
  e.stopImmediatePropagation();

  if (converting) {
    // A second Ctrl+V while the first is still converting. Swallowing it is the
    // point: letting it through would paste the same report twice, once flat and
    // once formatted.
    message.open({ key: TOAST_KEY, type: 'loading', content: 'Still rebuilding the formatting…', duration: 0 });
    return;
  }

  converting = true;
  message.open({ key: TOAST_KEY, type: 'loading', content: 'Rebuilding formatting…', duration: 0 });

  convertRichPaste({ rtf, docId: currentDocId ?? undefined })
    .then(response => {
      const converted = response?.data?.data?.html;
      if (!response?.data?.success || !converted) {
        throw new Error(response?.data?.message || 'The conversion produced nothing usable');
      }
      return requestInsert(inserter, converted);
    })
    .then(result => {
      if (result.ok) {
        message.open({ key: TOAST_KEY, type: 'success', content: 'Pasted with formatting', duration: 2 });
      } else {
        message.open({ key: TOAST_KEY, type: 'error', content: result.message, duration: 5 });
      }
    })
    .catch((error: any) => {
      // The clipboard still holds the report, so nothing is lost — point the
      // radiologist at the path that does not depend on any of this.
      const reason =
        error?.response?.data?.message || error?.message || 'Could not reach the server';
      message.open({
        key: TOAST_KEY,
        type: 'error',
        content: `${reason}. Use Plugins › Enhanced Paste to paste it manually.`,
        duration: 6,
      });
    })
    .finally(() => {
      converting = false;
    });
};

// ── Installing and removing the listener ─────────────────────────────────────

/**
 * Is this window an OnlyOffice editor document? Throws for a cross-origin frame,
 * which is the answer we want there anyway.
 */
const looksLikeDsEditor = (win: Window): boolean => {
  try {
    const doc = win.document;
    if (!doc) return false;
    if (doc.getElementById('editor_sdk')) return true;
    return /\/documenteditor\//i.test(win.location.pathname);
  } catch {
    return false; // cross-origin: DS is not same-origin here, so there is nothing to install
  }
};

const collectFrames = (win: Window, depth: number, out: Window[]) => {
  if (depth > 3) return;
  let count = 0;
  try {
    count = win.length;
  } catch {
    return;
  }
  for (let i = 0; i < count; i++) {
    let child: Window | null = null;
    try {
      child = win[i] as Window;
    } catch {
      continue;
    }
    if (!child) continue;
    out.push(child);
    // Recursion is guarded by the same try/catch: a cross-origin child reports
    // length 0 to us and the walk simply stops there.
    collectFrames(child, depth + 1, out);
  }
};

const scan = () => {
  const frames: Window[] = [];
  collectFrames(window, 0, frames);

  for (const win of frames) {
    if (hooked.has(win)) continue;
    if (!looksLikeDsEditor(win)) continue;
    try {
      win.addEventListener('paste', onPaste, true);
      hooked.set(win, () => {
        try {
          win.removeEventListener('paste', onPaste, true);
        } catch {
          /* frame is already gone */
        }
      });
    } catch {
      /* frame went away between the check and the install */
    }
  }

  // Drop editor windows that are no longer in the tree. The editor is destroyed
  // and rebuilt on every queue advance — holding each dead window forever would
  // pile them up across a reporting session, and the Map is a strong reference.
  const live = new Set(frames);
  for (const win of Array.from(hooked.keys())) {
    if (!live.has(win)) hooked.delete(win);
  }
};

/**
 * @param docId Id of the document this surface is editing; forwarded to the
 *              conversion endpoint for its log line only.
 */
const useOoPasteInterceptor = (docId?: number | string | null) => {
  currentDocId = docId;

  useEffect(() => {
    mounted += 1;

    if (mounted === 1) {
      window.addEventListener('message', handleInserterMessage);
      // Ask any inserter that came up before this surface mounted to say hello
      // again, rather than waiting for an editor reload to hear it.
      scan();
      hooked.forEach((_remover, win) => {
        try {
          findInserterWindow(win.document)?.postMessage({ type: 'pacs_inserter_ping' }, '*');
        } catch {
          /* frame is gone */
        }
      });
      scanTimer = setInterval(scan, SCAN_INTERVAL_MS);
    }

    return () => {
      mounted -= 1;
      if (mounted > 0) return;

      if (scanTimer) clearInterval(scanTimer);
      scanTimer = null;
      window.removeEventListener('message', handleInserterMessage);
      hooked.forEach(remove => remove());
      hooked.clear();
      pendingInserts.forEach(pending => window.clearTimeout(pending.timer));
      pendingInserts.clear();
    };
  }, []);
};

export default useOoPasteInterceptor;
